<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { TaskStatus } from '@/api';
import { useOfficeTemplateStore } from '@/office/stores/officeTemplateStore';
import { useTemplateValidationPolling } from '@/composables/contractGeneration/useTemplateValidationPolling';
import TemplateLibraryPanel from '@/office/components/templates/TemplateLibraryPanel.vue';
import TemplateEditorPanel from '@/office/components/templates/TemplateEditorPanel.vue';
import GaiaChatView from '@/components/GaiaChatView.vue';
import GaiaTabBar, { type GaiaTabBarItem } from '@/components/design/composite/GaiaTabBar.vue';

/**
 * Templates task-pane view, split by use case:
 * - "My templates": browse existing templates, open one into Word, inspect
 *   its stored fields.
 * - "Editor": author in the current document — insert fields, test validity,
 *   save back as template / variant / version.
 * - "Assistant": the GAIA chat locked to the template skillset — reads the
 *   document on demand and generates GAIA-Jinja snippets. Reuses the regular chat.
 */

type TemplatesTab = 'library' | 'editor' | 'chat';

const route = useRoute();

// Open the assistant directly when navigated here with `?tab=chat` (e.g. from
// the general chat's skillset picker); otherwise start on the library.
const activeTab = ref<TemplatesTab>(route.query.tab === 'chat' ? 'chat' : 'library');

const tabItems: GaiaTabBarItem[] = [
  { key: 'library', label: 'My templates' },
  { key: 'editor', label: 'Editor' },
  { key: 'chat', label: 'Assistant' },
];

function isTemplatesTab(key: string): key is TemplatesTab {
  return key === 'library' || key === 'editor' || key === 'chat';
}

function selectTab(key: string): void {
  if (isTemplatesTab(key)) activeTab.value = key;
}

function openTabFromQuery(): void {
  if (route.query.tab === 'chat') activeTab.value = 'chat';
}

onMounted(openTabFromQuery);

// Cover re-navigation here while already mounted (query changes without remount).
watch(() => route.query.tab, openTabFromQuery);

const store = useOfficeTemplateStore();

// Poll the selected file's async validation status here (not in a panel) so
// it keeps refreshing regardless of which sub-tab is active — e.g. saving
// from the Editor tab flips My templates' status to Ready by itself.
const isSelectedFileValidating = computed<boolean>(() => {
  const status = store.selectedVariant?.template_status;
  return status === TaskStatus.PENDING || status === TaskStatus.RUNNING;
});

useTemplateValidationPolling(isSelectedFileValidating, () =>
  store.refreshVariants({ silent: true })
);
</script>

<template>
  <n-flex vertical :size="14" :wrap="false">
    <gaia-tab-bar :items="tabItems" :active="activeTab" @select="selectTab" />

    <!-- The library re-mounts on every visit so it refreshes; the editor and
         the assistant stay mounted, keeping their drawers, drafts and thread. -->
    <template-library-panel v-if="activeTab === 'library'" />
    <div v-show="activeTab === 'editor'">
      <template-editor-panel />
    </div>
    <div v-show="activeTab === 'chat'" class="tc-chat-embed">
      <gaia-chat-view chat-kind="templating" embedded />
    </div>
  </n-flex>
</template>

<style scoped>
/* The embedded chat needs a bounded, scrolling height inside the task pane —
   Naive UI has no fixed-height scroll-region primitive, so this wrapper is CSS.
   100vh minus the office header (48), the tab row (~44), the layout's padding
   and the pinned bottom navigation. The :deep override forces the chat to fill
   this box — its standalone `height: calc(100vh-130px)` / `min-height: 640px`
   rules are for the full-page /gaia-chat route and would otherwise push the
   composer behind the bottom nav. */
.tc-chat-embed {
  height: calc(100vh - 196px);
  min-height: 320px;
}

.tc-chat-embed :deep(.gaia-chat-view) {
  height: 100%;
  min-height: 0;
}
</style>
